import { useState, useCallback } from "react";
import { GetInvoicesForCustomerUseCase } from "../../../../shared/domain/use-cases/GetInvoicesForCustomerUseCase";
import { InvoiceRepositoryImpl } from "../../../../shared/data/repositories/InvoiceRepositoryImpl";
import { useDropdownCache } from "../../../../shared/presentation/hooks/useDropdownCache";
import { InvoiceListItemEntity } from "../../../../shared/domain/entities/InvoiceListItemEntity";

export interface UseCustomerInvoicesReturn {
  invoices: InvoiceListItemEntity[];
  loading: boolean;
  error: string | null;
  fetchInvoices: (
    customerZohoId: string,
    searchTerm: string,
    limit: number,
    offset: number
  ) => Promise<{
    items: InvoiceListItemEntity[];
    hasMore?: boolean;
  }>;
  clearError: () => void;
  reset: () => void;
}

// Create repository and use case instances (singleton pattern)
const invoiceRepository = new InvoiceRepositoryImpl();
const getInvoicesForCustomerUseCase = new GetInvoicesForCustomerUseCase(
  invoiceRepository
);

/**
 * Hook for fetching invoices for a customer with pagination and caching
 * Supports SearchableDropdown fetchItems function signature
 */
export const useCustomerInvoices = (): UseCustomerInvoicesReturn => {
  const [invoices, setInvoices] = useState<InvoiceListItemEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getCached, setCached } = useDropdownCache();

  /**
   * Fetch invoices for a customer with pagination and caching
   * Supports SearchableDropdown fetchItems function signature
   */
  const fetchInvoices = useCallback(
    async (
      customerZohoId: string,
      searchTerm: string,
      limit: number,
      offset: number
    ): Promise<{
      items: InvoiceListItemEntity[];
      hasMore?: boolean;
    }> => {
      if (!customerZohoId || customerZohoId.trim() === "") {
        return {
          items: [],
          hasMore: false,
        };
      }

      // Cache key includes Zoho customer ID
      const cacheKey = { customerZohoId, searchTerm, limit, offset };
      const shouldCache = offset === 0 && !searchTerm;

      if (shouldCache) {
        const cached = getCached<{
          items: InvoiceListItemEntity[];
          hasMore?: boolean;
        }>("invoices", cacheKey);

        if (cached) {
          return cached;
        }
      }

      setLoading(true);
      setError(null);

      try {
        // Use the use case to fetch invoices
        const result = await getInvoicesForCustomerUseCase.execute(
          customerZohoId.trim(),
          limit,
          offset,
          searchTerm?.trim()
        );

        const items = result.items || [];
        const hasMore = result.pagination?.has_more || false;

        const response = {
          items,
          hasMore,
        };

        // Cache the result if it's the initial load
        if (shouldCache) {
          setCached("invoices", cacheKey, response);
        }

        return response;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to fetch invoices";
        setError(errorMessage);
        console.error("Failed to fetch invoices:", error);
        return {
          items: [],
          hasMore: false,
        };
      } finally {
        setLoading(false);
      }
    },
    [getCached, setCached]
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setInvoices([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    invoices,
    loading,
    error,
    fetchInvoices,
    clearError,
    reset,
  };
};
